import { useState, useEffect } from "react";
import { useApiClient } from "./useApiClient";
import { getTodayTimeRange } from "../utils/dateUtils";
import { apiRoutes } from "../api/routes";

export const useNotificationCount = (enabled = true) => {
  const [notificationCount, setNotificationCount] = useState<number>(0);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const apiClient = useApiClient();

  useEffect(() => {
    // Don't poll when the caller lacks alert_view (the indicator is hidden).
    if (!enabled) {
      setNotificationCount(0);
      setIsLoading(false);
      return;
    }

    let isMounted = true;
    let interval: ReturnType<typeof setInterval> | undefined;

    const fetchNotificationCount = async () => {
      setIsLoading(true);
      try {
        const { start, end } = getTodayTimeRange();
        const requestBody = {
          select: ["id"],
          query: [
            ["between", "timestamp", start, end],
            ["null", "resolve_timestamp"],
            [
              "in",
              "type",
              [
                0, 1, 2, 4, 5, 6, 13, 14, 15, 18, 19, 20, 21, 22, 23, 24, 25, 26, 28, 29, 30, 31, 32,
                33, 34, 35, 36, 37, 38, 39, 40, 41, 42, 100, 101,
              ],
            ],
          ],
          options: {
            timeout: 10,
            limit: 0,
            calculate_total: true,
            return_results: false,
            select_distinct: true,
          },
        };

        const response = await apiClient.post(apiRoutes.proxy("support/internal/alert/select_query"), requestBody);

        if (!isMounted) return;
        if (response.data && response.data.info) {
          setNotificationCount(response.data.info?.total_results);
        } else {
          setError("Failed to fetch notification count");
        }
      } catch (err: unknown) {
        if (!isMounted) return;
        // Stop polling permanently when unauthorized — mirrors legacy
        // alert_status.es6:135-137 (freq = -1 on 401).
        const status = (err as { response?: { status?: number } })?.response?.status;
        if (status === 401 || status === 403) {
          if (interval) clearInterval(interval);
          return;
        }
        if (err instanceof Error) {
          setError(err.message);
        } else {
          setError("An unknown error occurred");
        }
      } finally {
        if (isMounted) setIsLoading(false);
      }
    };

    fetchNotificationCount();
    interval = setInterval(fetchNotificationCount, 60000);

    return () => {
      isMounted = false;
      if (interval) clearInterval(interval);
    };
  }, [apiClient, enabled]);

  return { notificationCount, isLoading, error };
};
